import { useCallback } from 'react';
import {
  deleteField,
  doc,
  DocumentReference,
  Firestore,
  WriteBatch
} from 'firebase/firestore';
import { nestObject } from '@/lib/utils/firestore-nest';
import type {
  AnySummary,
  Novel,
  StorageMode
} from '@/lib/types';
import type { NovelDao } from '@/api/daos/novel-dao';

type ValidFamily = 'entities' | 'narrative';

export function useNovelBatch(
  novel: Novel,
  novelDao: NovelDao,
  firestore: Firestore,
  getItemRef: (family: ValidFamily, type: string, id: string) => DocumentReference | null
) {
  const addSaveToBatch = useCallback(
    <T extends { id: string }>(
      batch: WriteBatch,
      family: ValidFamily,
      type: string,
      item: T,
      isNew: boolean = false
    ) => {
      if (!novel.id) throw new Error('Novel not loaded');
      const layout = novel.storageLayout;
      const familyLayout = family === 'entities' ? layout.entities : layout.narrative;
      const mode: StorageMode = familyLayout[type]?.items || 'embedded';
      const ref = getItemRef(family, type, item.id);
      if (!ref) return;

      const cleanData: Record<string, unknown> = { ...item };
      Object.keys(cleanData).forEach(k => {
        if (cleanData[k] === undefined) delete cleanData[k];
      });

      if (mode === 'embedded') {
        const { id, ...rest } = cleanData;
        const updates: Record<string, unknown> = {};
        if (isNew) {
          updates[`entityList.${id}`] = cleanData;
        } else {
          Object.entries(rest).forEach(([key, value]) => {
            updates[`entityList.${id}.${key}`] = value;
          });
        }
        batch.set(ref, nestObject(updates), { merge: true });
      } else {
        if (isNew) {
          batch.set(ref, cleanData);
        } else {
          batch.set(ref, cleanData, { merge: true });
        }
      }
    },
    [novel, novelDao, getItemRef]
  );

  const addSummaryUpdateToBatch = useCallback(
    (
      batch: WriteBatch,
      family: ValidFamily,
      type: string,
      itemId: string,
      summary: AnySummary | null
    ) => {
      if (!novel.id) throw new Error('Novel not loaded');
      const layout = novel.storageLayout;
      const familyLayout = family === 'entities' ? layout.entities : layout.narrative;
      const metaMode: StorageMode = familyLayout[type]?.meta || 'embedded';
      const metaFamily = family === 'entities' ? 'entitiesMeta' : 'narrativeMeta';

      const cleanSummary: Record<string, unknown> | null = summary
        ? Object.entries(summary).reduce((acc, [key, value]) => {
          if (value !== undefined) acc[key] = value;
          return acc;
        }, {} as Record<string, unknown>)
        : null;

      if (metaMode === 'split') {
        const metaRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id, metaFamily, type);
        batch.set(metaRef, nestObject({
          [`summaries.${itemId}`]: cleanSummary || deleteField()
        }), { merge: true });
      } else {
        const novelRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id);
        batch.update(novelRef, {
          [`${metaFamily}.summaries.${type}.${itemId}`]: cleanSummary || deleteField()
        });
      }
    },
    [novel, novelDao, firestore]
  );

  return { addSaveToBatch, addSummaryUpdateToBatch };
}
